'use client'

import Text from '@/app/components/common/Text'
import Title from '@/app/components/common/Title'
import { Icons } from '@/app/components/ui/Icons'
import { useTranslations } from 'next-intl'
import React, { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
	children: ReactNode
	fallback?: ReactNode
}

interface State {
	hasError: boolean
	error: Error | null
}

interface ErrorBoundaryInnerProps extends Props {
	translations: {
		title: string
		message: string
		refreshButton: string
	}
}

// Inner class component that receives translations as props
class ErrorBoundaryInner extends Component<ErrorBoundaryInnerProps, State> {
	public state: State = {
		hasError: false,
		error: null,
	}

	public static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error }
	}

	public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error('Uncaught error:', error, errorInfo)
	}

	public render() {
		if (this.state.hasError) {
			if (this.props.fallback) {
				return this.props.fallback
			}

			const { translations } = this.props

			return (
				<div className="container mx-auto p-6">
					<div className="bg-text-100 rounded-4xl p-6 text-center">
						<Icons.iconAlert className="mx-auto mb-4 text-4xl text-score-bad" />
						<Title level="3" size="xl" color="text-900" bold>
							{translations.title}
						</Title>
						<Text className="mt-2 inline-flex text-text-700">{translations.message}</Text>
						<button
							onClick={() => window.location.reload()}
							className="mt-4 px-6 py-2 bg-primary text-white rounded-full hover:bg-primary-dark transition-colors"
						>
							{translations.refreshButton}
						</button>
					</div>
				</div>
			)
		}

		return this.props.children
	}
}

// Functional component wrapper that provides translations
const ErrorBoundaryWithTranslations: React.FC<Props> = ({
	children,
	fallback,
}) => {
	const t = useTranslations('Errors')

	// Prepare translations with fallbacks
	const translations = {
		title: t('errorBoundary.title'),
		message: t('errorBoundary.message'),
		refreshButton: t('errorBoundary.refreshButton'),
	}

	return (
		<ErrorBoundaryInner translations={translations} fallback={fallback}>
			{children}
		</ErrorBoundaryInner>
	)
}

export default ErrorBoundaryWithTranslations
